import { useCallback, useEffect, useMemo, useState } from "react";
import { LogReviewChartGrid } from "./LogReviewChartGrid";
import type { DrawerFilterFieldConfig } from "./LogReviewFilterDrawer";
import { LogReviewTable } from "./LogReviewTable";
import {
  LogReviewTopFilterBar,
  type LogReviewTopFilters,
} from "./LogReviewTopFilterBar";
import {
  fetchFilterOptions,
  type LogReviewChartFilters,
  type LogReviewFilterOptions,
} from "../../../api/logReviewApi";
import { useLogReviewCharts } from "../../../hooks/useLogReviewCharts";
import { useSirarFilterTree } from "../../../hooks/useSirarFilterTree";

const pad = (n: number) => `${n}`.padStart(2, "0");
const isoDate = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Default payload window — first day of the current month through today.
const defaultFrom = () => {
  const d = new Date();
  return isoDate(new Date(d.getFullYear(), d.getMonth(), 1));
};
const defaultTo = () => isoDate(new Date());

const uniq = (values: string[]) => Array.from(new Set(values));

/**
 * Log Review dashboard — the seven-tile chart grid over a server-driven
 * findings table, both scoped by the shared top filter bar.
 */
export const LogReviewPage = () => {
  const { tree, loading: treeLoading } = useSirarFilterTree();

  // Full option sets used when a level has no explicit selection.
  const allAppCodes = useMemo(
    () => uniq(tree.flatMap((d) => d.applications.map((a) => a.appCode))),
    [tree],
  );
  const allCategories = useMemo(
    () => uniq(tree.flatMap((d) => d.applications.flatMap((a) => a.categories))),
    [tree],
  );
  const appNameToCode = useMemo(() => {
    const map = new Map<string, string>();
    tree.forEach((d) => d.applications.forEach((a) => map.set(a.appName, a.appCode)));
    return map;
  }, [tree]);

  const toChartFilters = useCallback(
    (top: Partial<LogReviewTopFilters>): LogReviewChartFilters => {
      const apps = top.application ?? [];
      const cats = top.category ?? [];
      const from = top.dateRange?.from ?? defaultFrom();
      const to = top.dateRange?.to ?? defaultTo();
      return {
        appCode: apps.length
          ? apps.map((n) => appNameToCode.get(n)).filter((c): c is string => Boolean(c))
          : allAppCodes,
        category: cats.length ? cats : allCategories,
        startDate: `${from}T00:00:00.000`,
        endDate: `${to}T23:59:59.999`,
      };
    },
    [allAppCodes, allCategories, appNameToCode],
  );

  const [appliedTop, setAppliedTop] = useState<LogReviewTopFilters | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const filters = useMemo<LogReviewChartFilters>(
    () => toChartFilters(appliedTop ?? {}),
    [toChartFilters, appliedTop],
  );

  // Top-bar selection passed to the server-side findings list — adds the raw
  // division names on top of the chart filter payload.
  const listBaseParams = useMemo(
    () => ({
      appCode: filters.appCode,
      category: filters.category,
      // Division is intentionally sent as [] for now (list + tab-count API).
      division: [] as string[],
      startDate: filters.startDate,
      endDate: filters.endDate,
    }),
    [filters],
  );

  // Hold the first fetch until the filter cascade has applied (appliedTop set
  // by the bar's default-init) — or, if the tree resolved empty, once its load
  // settles. Prevents the mount fetch with empty filters that the initial
  // apply would otherwise abort and refire.
  const ready = appliedTop !== null || (!treeLoading && tree.length === 0);

  const charts = useLogReviewCharts(filters, reloadKey, ready);

  const handleApply = useCallback((top: LogReviewTopFilters) => {
    setAppliedTop(top);
    setReloadKey((k) => k + 1);
  }, []);

  const handleClear = useCallback(() => {
    setAppliedTop(null);
    setReloadKey((k) => k + 1);
  }, []);

  // Re-run the chart batch (+ table) from a tile's error-state Retry button.
  const handleRetry = useCallback(() => setReloadKey((k) => k + 1), []);

  // Table drawer filter options (risk / teams / status) — shared by all tabs.
  const [filterOptions, setFilterOptions] = useState<LogReviewFilterOptions>({
    risk: [],
    teams: [],
    status: [],
  });

  useEffect(() => {
    const controller = new AbortController();
    fetchFilterOptions(controller.signal)
      .then(setFilterOptions)
      .catch(() => {
        /* leave options empty on failure — the drawer still opens */
      });
    return () => controller.abort();
  }, []);

  const filterFields: DrawerFilterFieldConfig[] = useMemo(
    () => [
      { key: "risk", label: "Risk", options: filterOptions.risk },
      { key: "team", label: "Team", options: filterOptions.teams },
      { key: "status", label: "Status", options: filterOptions.status },
    ],
    [filterOptions],
  );

  // Body only: the page title, strapline and tab strip belong to the CAG
  // shell, which renders this through an <Outlet />.
  return (
    <div className="flex flex-col gap-4">
      <LogReviewTopFilterBar
        divisions={tree}
        onApply={handleApply}
        onClear={handleClear}
      />

      <LogReviewChartGrid
        complianceStatus={charts.complianceStatus}
        nonCompliantBreakdown={charts.distribution}
        findingsTrend={charts.findingsTrend}
        workflowStatus={charts.workflowStatus}
        byApplications={charts.byApplications}
        byTeam={charts.byTeam}
        byCrCategories={charts.byCrCategories}
        loading={charts.loading}
        error={charts.error}
        onRetry={handleRetry}
      />

      <LogReviewTable
        filterFields={filterFields}
        listBaseParams={listBaseParams}
        listReloadKey={reloadKey}
        enabled={ready}
      />
    </div>
  );
};
